import {Button} from '@tryghost/shade';
import {DeleteSiteModal} from './DeleteSiteModal';
import {LucideIcon, formatNumber} from '@tryghost/shade/utils';
import {SITE_DELETION_PENDING_MESSAGE, getSSOLink} from '@/api/sites';
import {getSitePublicUrl} from '@/lib/site-url';
import {useDeleteSite} from '@/hooks/useDeleteSite';
import {useNavigate} from 'react-router-dom';
import {useRetrySite} from '@/hooks/useRetrySite';
import {useState} from 'react';
import type {SiteWithAccess} from '@/types/api';
import type {SiteWithDeletion} from '@/api/sites';

const DEFAULT_COVER_URL = 'https://static.ghost.org/v4.0.0/images/feature-image.jpg';

function isFailedSite(site: SiteWithAccess): boolean {
    return site.status === 'failed';
}

export function SiteCard({site, index}: { site: SiteWithDeletion; index: number }) {
    const navigate = useNavigate();
    const retrySite = useRetrySite();
    const deleteSite = useDeleteSite();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [ssoLoading, setSsoLoading] = useState(false);
    const [iconError, setIconError] = useState(false);
    const [failedCoverUrls, setFailedCoverUrls] = useState<string[]>([]);
    const [deletionStarted, setDeletionStarted] = useState(false);
    const deletionPending = deletionStarted || site.deletion_pending;

    const siteUrl = site.custom_domain || `${site.slug}.tribetale.com`;
    const publicUrl = getSitePublicUrl(site);
    const failed = isFailedSite(site);
    const coverUrl = [site.coverImageUrl, DEFAULT_COVER_URL].find(url => url && !failedCoverUrls.includes(url));

    const openNewsletterAdmin = async () => {
        if (ssoLoading) {
            return;
        }

        setSsoLoading(true);
        // Open synchronously to avoid popup blockers during SSO.
        const adminTab = window.open('', '_blank');
        if (adminTab) {
            adminTab.opener = null;
        }
        try {
            const url = await getSSOLink(site.id);
            if (adminTab) {
                adminTab.location.href = url;
            } else {
                window.open(url, '_blank');
            }
        } catch {
            const retryUrl = new URL(`/sites/${site.id}/open?error=sso`, window.location.origin).href;
            if (adminTab) {
                adminTab.location.href = retryUrl;
            } else {
                navigate(`/sites/${site.id}/open?error=sso`);
            }
        } finally {
            setSsoLoading(false);
        }
    };

    const handleClick = async () => {
        if (failed || deletionPending) {
            return;
        }
        if (site.status === 'provisioning') {
            navigate(`/sites/${site.id}/provisioning`);
            return;
        }
        if (site.status !== 'active') {
            // Inactive tenants need billing access before they can publish.
            navigate('/billing');
            return;
        }

        await openNewsletterAdmin();
    };

    const handleRetry = (e: React.MouseEvent) => {
        e.stopPropagation();
        retrySite.mutate(site.id, {
            onSuccess: () => {
                navigate(`/sites/${site.id}/provisioning`);
            }
        });
    };

    const handleSettingsClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        navigate(`/sites/${site.id}/settings`);
    };

    const handleDeleteClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = () => {
        deleteSite.mutate(site.id, {
            onSuccess: (result) => {
                setDeletionStarted(result?.status === 'deletion_pending');
                setShowDeleteModal(false);
            }
        });
    };

    // Publication settings (the address lives there) are owner-only on the server.
    const canManage = site.access_type === 'owner' && site.status === 'active' && !deletionPending;

    const actionLabel = ssoLoading ? 'Opening…' : site.status === 'provisioning' ? 'View setup progress' : site.status === 'active' ? 'Open publication' : 'Manage billing';

    return (
        <>
            <article className={`studio-publication-card${failed ? ' is-failed' : ''}`} onClick={handleClick}>
                <button aria-label={`${actionLabel}: ${site.name}`} className="studio-card-top" disabled={failed || deletionPending || ssoLoading} type="button" onClick={(event) => {
                    event.stopPropagation();
                    void handleClick();
                }}>
                    {coverUrl ? <img alt={`${site.name} cover`} className="studio-publication-cover-image" decoding="async" loading="lazy" src={coverUrl} onError={() => setFailedCoverUrls(urls => [...urls, coverUrl])} /> : <span className="studio-cover-fallback">{site.name}</span>}
                    {!failed && !deletionPending && <span aria-hidden="true" className="studio-preview-open"><LucideIcon.ArrowUpRight size={16} strokeWidth={1.5} /></span>}
                </button>
                <div className="studio-publication-identity">
                    {site.iconUrl && !iconError ? <img alt="" className="studio-publication-icon" src={site.iconUrl} onError={() => setIconError(true)} /> : <span aria-hidden="true" className="studio-publication-icon studio-publication-number">{formatNumber(index)}</span>}
                    <div className="studio-title-group">
                        <h2>{site.name}</h2>
                        <a className="studio-site-url" href={publicUrl} rel="noopener noreferrer" target="_blank" onClick={event => event.stopPropagation()}>{siteUrl}</a>
                        {site.access_type === 'team_member' && <span className="studio-access-label">Shared · {site.team_role || 'Team member'}</span>}
                    </div>
                </div>
                <div className="studio-publication-actions">
                    {failed ? <>
                        <Button className="studio-open-publication" disabled={deletionPending || retrySite.isLoading} type="button" variant="ghost" onClick={handleRetry}>{retrySite.isLoading ? 'Retrying...' : 'Retry'}</Button>
                        <Button className="studio-delete-publication" disabled={deletionPending} type="button" variant="ghost" onClick={handleDeleteClick}>Delete</Button>
                    </> : <span style={{display: 'flex', alignItems: 'center', gap: 16, minWidth: 0}}>
                        <Button className="studio-open-publication" disabled={deletionPending || ssoLoading} type="button" variant="ghost" onClick={(event) => {
                            event.stopPropagation();
                            void handleClick();
                        }}>{actionLabel}<LucideIcon.ArrowRight size={15} /></Button>
                        {canManage && (
                            <Button className="studio-open-publication" disabled={deletionPending} type="button" variant="ghost" onClick={handleSettingsClick}>Settings</Button>
                        )}
                    </span>}
                    {deletionPending ? (
                        <span className="studio-access-label">Deletion pending</span>
                    ) : site.status === 'active' ? (
                        <a aria-label={`Visit ${site.name} website — live`} className="studio-visit-publication" href={publicUrl} rel="noopener noreferrer" target="_blank" title="Visit live website" onClick={event => event.stopPropagation()}>
                            <span>Live</span>
                        </a>
                    ) : (
                        <span aria-label={`Publication status: ${site.status}`} className={`studio-status studio-status-${site.status}`}>
                            {site.status === 'provisioning' ? 'Setting up' : site.status.charAt(0).toUpperCase() + site.status.slice(1)}
                        </span>
                    )}
                </div>
                {deletionPending && <p className="studio-page-description" role="status">{SITE_DELETION_PENDING_MESSAGE}</p>}
            </article>
            {showDeleteModal && !deletionPending && <DeleteSiteModal isLoading={deleteSite.isLoading} siteName={site.name} onCancel={() => setShowDeleteModal(false)} onConfirm={handleDeleteConfirm} />}
        </>
    );
}
